"use client";

import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { CONSENT_OPEN_EVENT, type ConsentRecord } from "@/lib/consent";
import { useConsent } from "@/lib/consent/hooks";
import { acceptAll, rejectAll } from "@/lib/consent/storage";
import { entryAttribution, trackConsentEvent } from "@/lib/consent/track";
import { isJobDetailPath } from "@/lib/jobs/is-job-detail-path";
import { CookiePreferencesModal } from "./cookie-preferences-modal";

/**
 * When ON, EEA/UK/CH visitors get Google's certified CMP (Funding Choices) banner
 * — injected by the AdSense tag — instead of this first-party banner, so they
 * aren't shown two consent prompts. OFF/absent (the default) = this banner shows
 * everywhere, exactly as before. The flag is the SOLE switch and must be flipped
 * to ON only once the Google CMP message is PUBLISHED in AdSense; until then,
 * suppressing here would leave EEA visitors with no consent UI at all.
 */
const EEA_CMP_FLAG = "eea-google-cmp";

type Strings = {
	heading: string;
	body: string;
	accept: string;
	reject: string;
	customize: string;
	privacy: string;
	more: string;
	less: string;
};

const EN: Strings = {
	heading: "We use cookies to improve Loker Dollar",
	body: "Necessary cookies keep you signed in, and we use privacy-friendly first-party product analytics — including session replay with all form input masked, no name or email — on a legitimate-interest basis to keep the site working well (this is opt-in where the law requires it, such as the EU and UK). Marketing cookies and linking analytics to your email are used only if you accept. You can change this any time.",
	accept: "Accept all",
	reject: "Reject all",
	customize: "Customize",
	privacy: "Privacy policy",
	more: "More info",
	less: "Less info",
};

const ID: Strings = {
	heading: "Kami menggunakan cookie untuk meningkatkan Loker Dollar",
	body: "Cookie yang diperlukan menjaga Anda tetap masuk, dan kami menggunakan analitik produk pihak pertama yang ramah privasi — termasuk rekaman sesi dengan seluruh input formulir disamarkan, tanpa nama atau email — atas dasar kepentingan sah untuk menjaga situs berjalan baik (bersifat opt-in di wilayah yang mewajibkannya, seperti UE dan Inggris). Cookie marketing dan pengaitan analitik dengan email Anda hanya digunakan jika Anda menyetujui. Anda dapat mengubah ini kapan saja.",
	accept: "Terima semua",
	reject: "Tolak semua",
	customize: "Atur",
	privacy: "Kebijakan privasi",
	more: "Info selengkapnya",
	less: "Sembunyikan",
};

export type CookieBannerProps = { locale?: string };

export function CookieBanner({ locale }: CookieBannerProps) {
	const t = locale === "id" ? ID : EN;
	const base = `/${locale === "id" ? "id" : "en"}`;
	const { record, hydrated } = useConsent();
	const [prefsOpen, setPrefsOpen] = useState(false);
	const [bodyExpanded, setBodyExpanded] = useState(false);
	const [initialChoices, setInitialChoices] = useState<
		ConsentRecord["choices"] | undefined
	>(undefined);
	const bannerSeenRef = useRef(false);
	const posthog = usePostHog();
	// Job detail: anchor bottom-left so the card never covers the sticky Apply
	// card on the right (replay scan 01a12825, 2026-10-11).
	const onJobDetail = isJobDetailPath(usePathname());
	// `null` = not resolved yet. The Google-CMP handoff stays off until BOTH the
	// flag resolves to true AND the region resolves to a CMP country.
	const [cmpEnabled, setCmpEnabled] = useState<boolean | null>(null);
	const [cmpRegion, setCmpRegion] = useState<boolean | null>(null);

	useEffect(() => {
		const open = () => {
			setInitialChoices(record?.choices);
			setPrefsOpen(true);
		};
		window.addEventListener(CONSENT_OPEN_EVENT, open);
		return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
	}, [record]);

	// Resolve the EEA-CMP flag. Without a PostHog provider (tests, flag absent) this
	// stays false → no behavioural change.
	useEffect(() => {
		if (!posthog) return;
		const check = () =>
			setCmpEnabled(posthog.getFeatureFlag(EEA_CMP_FLAG) === true);
		check();
		posthog.onFeatureFlags(check);
	}, [posthog]);

	// Only when the CMP is enabled, resolve whether THIS visitor is in a region the
	// Google CMP serves. Fails OPEN (show our banner) on any error — never leave a
	// visitor with no consent UI.
	useEffect(() => {
		if (cmpEnabled !== true) return;
		let cancelled = false;
		fetch("/api/privacy-region")
			.then((r) => (r.ok ? r.json() : null))
			.then((d: unknown) => {
				const inCmpRegion =
					d && typeof d === "object"
						? (d as { cmpRegion?: unknown }).cmpRegion === true
						: false;
				if (!cancelled) setCmpRegion(inCmpRegion);
			})
			.catch(() => {
				if (!cancelled) setCmpRegion(false);
			});
		return () => {
			cancelled = true;
		};
	}, [cmpEnabled]);

	// Suppress the first-party banner when Google's CMP owns consent for this
	// visitor (flag on + CMP region). While the region is still resolving under an
	// enabled flag, hold the banner rather than flash it then hide it.
	const cmpHandlesConsent = cmpEnabled === true && cmpRegion === true;
	const cmpResolving = cmpEnabled === true && cmpRegion === null;
	const showBanner =
		hydrated &&
		record === null &&
		!prefsOpen &&
		!cmpHandlesConsent &&
		!cmpResolving;

	if (!hydrated) return null;

	if (
		showBanner &&
		!bannerSeenRef.current &&
		!(typeof navigator !== "undefined" && navigator.webdriver === true)
	) {
		bannerSeenRef.current = true;
		trackConsentEvent("consent_banner_shown", {
			surface: "banner",
			locale: locale === "id" ? "id" : "en",
			...entryAttribution(),
		});
	}

	return (
		<>
			{showBanner ? (
				<section
					data-testid="cookie-banner"
					aria-label={t.heading}
					// @sticky-slot-ok: consent banner must outrank everything, legal requirement.
					// Bottom-right corner card on sm+ (never a full-width centered overlay
					// that can sit over centered page content); full-width compact bar on
					// mobile. It stacks ABOVE a page-owned fixed bottom bar (job-detail
					// apply bar, pricing sticky CTA) by reading the height that bar
					// publishes as --sticky-cta-h (0px when none) — the bar never moves,
					// the banner does (sticky-cta-height.ts explains the CLS reason).
					className={`fixed inset-x-0 bottom-[var(--sticky-cta-h,0px)] z-40 px-3 pb-3 sm:inset-x-auto ${onJobDetail ? "sm:left-4 sm:right-auto" : "sm:right-4 sm:left-auto"} sm:bottom-[calc(var(--sticky-cta-h,0px)+1rem)] sm:w-[380px] sm:px-0 sm:pb-0 animate-in slide-in-from-bottom-4 duration-300`}
				>
					{/* min-h: pins the card's own footprint so an async source (the
					    EEA-CMP flag/region resolution above, or a locale swap) never
					    grows/shrinks this box after its first paint — a fixed-position
					    element still contributes its OWN rect delta to CLS if its content
					    reflows post-mount, even though it never pushes surrounding page
					    content (P1-5, mobile CLS 0.167 → <0.10; see
					    docs/plans/backlog/product-signal-audit-remediation-2026-08-04.md). */}
					<div className="w-full bg-card border border-border/60 rounded-2xl shadow-[0_5px_30px_rgba(0,0,0,0.18)] p-3.5 min-h-[104px]">
						<div className="flex items-center justify-between gap-2">
							<p className="min-w-0 flex-1 text-sm font-semibold tracking-tight">
								{t.heading}
							</p>
							<button
								type="button"
								onClick={() => setBodyExpanded((v) => !v)}
								aria-expanded={bodyExpanded}
								aria-controls="cookie-banner-explainer"
								className="sm:hidden shrink-0 text-xs font-medium text-primary hover:underline underline-offset-4 py-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
							>
								{bodyExpanded ? t.less : t.more}
							</button>
						</div>
						<p
							id="cookie-banner-explainer"
							className={`text-xs text-muted-foreground mt-1.5 leading-relaxed break-words ${
								bodyExpanded ? "block" : "hidden"
							} sm:block`}
						>
							{t.body}{" "}
							<a
								href={`${base}/privacy`}
								className="text-primary underline underline-offset-4"
							>
								{t.privacy}
							</a>
							.
						</p>
						<div className="flex flex-row flex-wrap items-center gap-2 mt-3">
							<button
								type="button"
								onClick={() => {
									setInitialChoices(undefined);
									setPrefsOpen(true);
								}}
								className="text-sm font-semibold tracking-tight text-foreground hover:text-primary transition-colors py-2.5 px-2 cursor-pointer order-3 sm:order-1"
							>
								{t.customize}
							</button>
							<button
								type="button"
								onClick={() => {
									const next = rejectAll();
									trackConsentEvent("consent_rejected", {
										surface: "banner",
										trigger: "reject_all",
										choices: next.choices,
										locale: locale === "id" ? "id" : "en",
									});
								}}
								className="text-sm border border-border/60 bg-transparent text-foreground font-semibold tracking-tight py-2.5 px-5 rounded-full hover:bg-muted/50 transition-colors cursor-pointer order-2"
							>
								{t.reject}
							</button>
							<button
								type="button"
								onClick={() => {
									const next = acceptAll();
									trackConsentEvent("consent_accepted", {
										surface: "banner",
										trigger: "accept_all",
										choices: next.choices,
										locale: locale === "id" ? "id" : "en",
									});
								}}
								className="text-sm bg-primary text-primary-foreground font-semibold tracking-tight py-2.5 px-5 rounded-full hover:bg-primary/90 transition-colors cursor-pointer order-1 sm:order-3"
							>
								{t.accept}
							</button>
						</div>
					</div>
				</section>
			) : null}
			<CookiePreferencesModal
				isOpen={prefsOpen}
				onClose={() => setPrefsOpen(false)}
				locale={locale}
				initial={initialChoices}
			/>
		</>
	);
}
